import { LegacyAntdModal } from 'components/LegacyAntdModal/LegacyAntdModal'
import cx from 'classnames'
import { TOC_CONTAINER_ID } from 'common/Prose/components/TOC/PortalTOC'
import RedirectModal from 'components/Onboarding/RedirectModal/RedirectModal'
import { exitOnboarding } from 'components/OnboardingTooltip/OnboardingUtils'
import { useAuth } from 'hooks/UseAuth'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import { focusOnEditorWhenAParentElementIsClicked } from 'proseShared/utils/focusOnEditorWhenAParentElementIsClicked'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDocStore } from 'stores/DocStore'
import { DocMode } from 'stores/DocStore.types'
import { ArticleFlat, ArticleView, Layer, RootState, UserRole } from 'types'
import { canRenderCoverPhoto, hasNonDefaultCoverImage } from 'utils/articleUtils'
import {
  EDITOR_CONTAINER_ID,
  MAIN_COLUMN_CONTAINER_ID,
  MAIN_LEFT_COLUMN_ID,
  MAIN_RIGHT_COLUMN_ID,
} from '../../Constants'
import Styles from '../../ProseEdit.module.scss'
import { CoverPhoto } from '../CoverPhoto/CoverPhoto'
import { NerudaEditor } from '../NerudaEditor/NerudaEditor'
import { TaxonomyEditContainer } from '../TaxonomyEdit/TaxonomyEditContainer'
import { ErrorBoundary } from '@sentry/react'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useFocusWithin } from '@react-aria/interactions'
import { useArticleViewLink } from 'hooks/useArticleViewLink'
import { useAbility } from '@casl/react'
import { AbilityContext } from 'components/Can'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useHistory } from 'react-router-dom'
import { useArticleViewChange } from 'components/ArticleViewSelect/useArticleViewChange'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { refreshSnapshot } from 'screens/Calendar/FullScreenPreview/useCalendarItem'

interface Props {
  article: ArticleFlat
  canEdit?: boolean
  layer: Layer
  withCommentViewOnly?: boolean
  leftRailOpen: boolean
}

const ArticleEditor = observer(({ article, canEdit, layer, withCommentViewOnly, leftRailOpen }: Props) => {
  const history = useHistory()
  const workspace = useCurrentWorkspace()
  const auth = useAuth()
  const queryClient = useQueryClient()
  const { articleMeta, showSnippetsRail } = useSelector(
    (state: RootState) => ({
      articleMeta: state.articleMeta[article.id],
      showSnippetsRail: state.snippets.show,
    }),
    isEqual,
  )
  const docStore = useDocStore()
  const isOwner = auth && docStore.owner?.id === auth.id
  const isArticleVersionControlled = layer.is_main && docStore.isVersionControlled && !isOwner
  const { currentViewRef } = useProseInstance()
  const onColumnContainerClick = focusOnEditorWhenAParentElementIsClicked({
    editorView: currentViewRef.current,
    parentElement: document.querySelector(`.${Styles.columnContainer}`),
  })
  const { how_to_use_this: howToUseThis, made_public: madePublic, why_trust_this: whyTrustThis } = article
  const { isMobile } = useResponsiveDesign()
  const [redirectIsArticle, setRedirectIsArticle] = useState(false)
  const [redirectArticleId, setRedirectArticleId] = useState<string>('')
  const [redirectModalVisible, setRedirectModalVisible] = useState(true)
  const changeArticleView = useArticleViewChange()
  const closeRedirectModal = () => {
    setRedirectModalVisible(false)
    exitOnboarding('redirect_modal')
  }
  const docModeIsEditor = docStore.docMode === DocMode.Editor
  let blurringGrace: NodeJS.Timeout | null = null

  let { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => {
      if (!isFocusWithin) {
        blurringGrace = setTimeout(() => docStore.blur(), 250)
      } else {
        blurringGrace && clearTimeout(blurringGrace)
        docStore.focus()
      }
    },
  })

  useEffect(() => {
    return () => {
      docStore.blur()
      if (article?.almanac_file?.asset_type !== 'Article' || !workspace?.id) return

      void refreshSnapshot({ id: article.id }).then(() => queryClient.invalidateQueries(calendarKeys.all))
    }
  }, [docStore, article, workspace, queryClient])

  useEffect(() => {
    if (!auth || !auth.meta) return
    if (!(article.initial_article && auth.meta.show_onboarding_redirect_modal)) return

    const { redirect_after_signup } = auth

    if (redirect_after_signup && redirect_after_signup.match(/^\/docs\/\S+/g)) {
      setRedirectIsArticle(true)
      setRedirectArticleId(redirect_after_signup.split('/')[2].split('-').pop() as string)
    }
  }, [auth, article.initial_article])

  // If accessing a publicly shared doc without belonging to the workspace, none of the editing
  // options are available, so switch to commenting mode
  useEffect(() => {
    if (articleMeta?.main_layer.public && workspace?.role === UserRole.UNAUTHED)
      changeArticleView(ArticleView.Commenting)
  }, [articleMeta?.main_layer.public, changeArticleView, workspace?.role])

  const editLinkProps = useArticleViewLink()
  const ability = useAbility(AbilityContext)
  const authedArticle = {
    role: workspace?.role || UserRole.UNAUTHED,
    permissions: articleMeta?.almanac_file.permissions,
    __caslSubjectType__: 'Article',
  }

  const handleDoubleClick = () => {
    if (ability.can('edit', authedArticle)) {
      history.push({ ...editLinkProps.to })
    }
    return false
  }

  const editorProps = withCommentViewOnly
    ? {
        handleDoubleClick,
      }
    : undefined

  return (
    <>
      {articleMeta && canRenderCoverPhoto(articleMeta) && <CoverPhoto articleMeta={articleMeta} editAccess={canEdit} />}
      <div
        className={cx(Styles.columnContainer, {
          [Styles.columnContainer__withImage]: hasNonDefaultCoverImage(article),
        })}
        onClick={onColumnContainerClick}
        id={MAIN_COLUMN_CONTAINER_ID}
        {...focusWithinProps}
      >
        <div className={Styles.leftColumn} id={MAIN_LEFT_COLUMN_ID}>
          <div className={Styles.nerudaTocContainer} id={TOC_CONTAINER_ID} />
        </div>
        <div className={Styles.midColumn}>
          <div className={Styles.contentContainer}>
            <div
              id={EDITOR_CONTAINER_ID}
              className={cx(Styles.editorContainer, Styles.withAiBar, {
                [Styles.locked]: isArticleVersionControlled,
              })}
              data-testid={EDITOR_CONTAINER_ID}
            >
              <div className={article.cover_image_url ? Styles.introContainer : Styles.introContainerNoCover}>
                <div
                  className={cx(Styles.emojiRow, {
                    [Styles.emojiRow__coverAndPhoto]: hasNonDefaultCoverImage(article),
                  })}
                ></div>
              </div>
              {madePublic && (
                <TaxonomyEditContainer
                  article={{ howToUseThis, whyTrustThis, ...article }}
                  className={Styles.taxonomyEdit}
                />
              )}
              <NerudaEditor
                article={article}
                layer={layer}
                inCommentMode={withCommentViewOnly}
                mode="edit"
                readOnly={isArticleVersionControlled}
                rightRailProps={{
                  show: !isMobile,
                  showSnippetsRail,
                  docModeIsEditor,
                  leftRailOpen,
                }}
                editorProps={editorProps}
              />
              {redirectIsArticle && (
                <LegacyAntdModal
                  wrapClassName="redirectAfterSignUpModal"
                  mask={false}
                  visible={redirectModalVisible}
                  onCancel={closeRedirectModal}
                >
                  <RedirectModal redirect_article_id={redirectArticleId} />
                </LegacyAntdModal>
              )}
            </div>
          </div>
        </div>
        <div className={Styles.rightColumn} id={MAIN_RIGHT_COLUMN_ID} />
      </div>
    </>
  )
})

const WithError = (props: Props) => (
  <ErrorBoundary
    onError={() =>
      showErrorMessageToast(
        'Failed to display document. Please try to refresh the page. If this error persists, please contact support.',
      )
    }
  >
    <ArticleEditor {...props} />
  </ErrorBoundary>
)

export { WithError as ArticleEditor }
